// HomePage — spec §Design (Home page sections) + §6 boundary values, amended by the Home
// live-slip strip spec (2026-09-20) and the per-league schedule window (#1082, 2026-09-21).
// Single job: compose the post-login landing view (score rail, state-driven hero,
// league-switched schedule card, The Wire) from App-owned data passed as props. It reads
// games/bets/oddsData/dataSource/syncLoading/syncError/onRetrySync, derives everything
// bet-related through buildHomeModel (src/lib/home-model.js), and owns only the local
// schedule-league toggle. The ONE fetching hook it calls is useLeagueSchedule -- a
// card-local, non-realtime read of a WEEKLY league's slate (NFL), kept out of App /
// useGameCacheRealtime on purpose (their 3-day window feeds the strip, rail and settlement
// display). Writes: one localStorage key (the chip, HOME_LEAGUE_KEY via
// league-preference.js) -- no DB, no API.
import { useMemo, useState } from "react";
import { JT } from "../components/ui/jumbotronTokens";
import LiveSlipStrip from "../components/ui/LiveSlipStrip";
import ImportNudge from "../components/ui/ImportNudge";
import { SyncErrorCard } from "../components/ui/SyncState";
import { buildHomeModel } from "../lib/home-model";
import { relativeAge } from "../lib/relativeAge";
import { openInAppBrowser } from "../lib/platform";
import { SUPPORTED_LEAGUES } from "../lib/constants";
import { leagueLabel, leagueLogoUrl } from "../lib/league-display";
import { HOME_LEAGUE_KEY, readLeaguePref, writeLeaguePref } from "../lib/league-preference";
import { scheduleCadence, scheduleWindow, localDateKey } from "../lib/league-schedule";
import { useLeagueSchedule } from "../hooks/useLeagueSchedule";

// League toggle: any SUPPORTED_LEAGUES value passes through; anything else fails
// closed to mlb (spec §6). No league key is special-cased here -- the registry
// is the only league list on the client (design intention 2026-09-16).
export function normalizeLeague(league) {
  return SUPPORTED_LEAGUES.includes(league) ? league : "mlb";
}

const SCHEDULE_STATUSES = new Set(["LIVE", "UPCOMING", "FINAL", "OFF"]);
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
// Row order inside a day, and therefore which rows survive the per-day cap: what is on
// now, then what is next by start time, then what is over. game_cache returns rows in
// physical order, so without this a LIVE game could be the one behind "+N more".
const STATUS_RANK = { LIVE: 0, UPCOMING: 1, FINAL: 2, OFF: 2 };
// startTimeUTC (ISO, from raw_data) orders correctly as a plain string. A row without
// it keys as "" and ties -- the sort is stable, so ties keep the server order
// (.order("game_start_time") in useLeagueSchedule / seed order for realtime rows).
// Never the local display string: "01:00 PM" < "10:00 AM" lexically. Plain comparison,
// not localeCompare: ICU collation ignores ":".
const startKey = (g) => (typeof g.startTimeUTC === "string" ? g.startTimeUTC : "");
const byStatusThenStart = (a, b) => (STATUS_RANK[a.status] - STATUS_RANK[b.status]) || (startKey(a) < startKey(b) ? -1 : startKey(a) > startKey(b) ? 1 : 0);

// Local "today" as YYYY-MM-DD to match game.gameDate. useGameData intentionally
// supplies yesterday+today+tomorrow (UTC-rollover guard, hotspot #9); a DAILY
// league's card restricts to today so "GAMES TODAY" is not inflated by adjacent
// days, a WEEKLY league's card shows its rolling scheduleWindow (#1082).
function localToday() {
  return localDateKey(new Date());
}

// Day-group label for the weekly body: TODAY, else "SUN · 9/27". Local calendar
// maths on the YYYY-MM-DD key (no Date parsing of the key -> no UTC shift).
function dayLabel(dateKey, today) {
  if (dateKey === today) return "TODAY";
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${dt.toLocaleDateString([], { weekday: "short" }).toUpperCase()} · ${m}/${d}`;
}

// Defensive short-time format for the stale indicator. lastUpdated is a Date
// (useGameCacheRealtime sets `new Date()`); tolerate strings/invalid without crashing.
function fmtUpdated(d) {
  if (!d) return "";
  try {
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function Eyebrow({ children, style }) {
  // DESIGN.md Label role at the readable floor (UI shell + token floor, 2026-09-20).
  return <div style={{ ...JT.type.label, letterSpacing: "1.5px", color: JT.text.tertiary, ...style }}>{children}</div>;
}

// `legCount` > 0 marks a game the user has legs on: an N LEGS chip on its own row under the
// teams and the hero border. No status color -- "mine" is a fact, not a signal (Signal Scarcity). A LIVE
// game keeps its live border. Cards stay inert (spec §3).
function RailCard({ game, legCount = 0 }) {
  const live = game.status === "LIVE";
  const final = game.status === "FINAL";
  const mine = legCount > 0;
  return (
    <div data-testid="rail-card" style={{
      flex: "0 0 auto", width: 112, borderRadius: 14, padding: "10px 12px",
      background: live ? JT.bg.well : JT.bg.panel,
      border: `1px solid ${live ? JT.status.live.border : mine ? JT.border.hero : JT.border.default}`,
    }}>
      {live ? (
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: JT.status.live.color, animation: "pulse 1.5s infinite" }} />
          <span style={{ ...JT.type.label, color: JT.status.live.color, overflow: "hidden", textOverflow: "ellipsis" }}>LIVE{game.quarter ? ` · ${game.quarter}` : ""}</span>
        </div>
      ) : (
        <Eyebrow style={{ letterSpacing: "1px", marginBottom: 6 }}>
          {final ? "FINAL" : game.startTime || "—"}
        </Eyebrow>
      )}
      {[game.away, game.home].map((t, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 800, color: JT.text.primary, marginTop: i ? 3 : 0 }}>
          <span>{t?.team || "—"}</span>
          <span>{(live || final) ? (t?.score ?? "") : ""}</span>
        </div>
      ))}
      {/* Own row (Ultron F4): the 88px header cannot hold "LIVE · TOP 6" or "07:05 PM" beside a chip. */}
      {mine && (
        <div style={{ marginTop: 6 }}>
          <span data-testid="rail-legs" style={{ ...JT.type.label, display: "inline-block", color: JT.text.secondary, background: JT.bg.tile, borderRadius: JT.radius.chip, padding: "1px 5px" }}>
            {legCount} {legCount === 1 ? "LEG" : "LEGS"}
          </span>
        </div>
      )}
    </div>
  );
}

export default function HomePage({ games, statusGames, bets, oddsData, dataSource, lastUpdated, wire = [], onViewBets, onImport, syncLoading = false, syncError = false, onRetrySync }) {
  // The chip persists under its own storage key (UI critique item E): HomePage unmounts on
  // every tab switch, so local state alone forgot it. Nothing valid stored -> normalizeLeague
  // -> mlb (spec §6). Writes go through league-preference (validated, platform.js storage).
  const [league, setLeagueState] = useState(() => normalizeLeague(readLeaguePref(HOME_LEAGUE_KEY)));
  const setLeague = (l) => { setLeagueState(l); writeLeaguePref(HOME_LEAGUE_KEY, l); };
  const activeLeague = normalizeLeague(league);
  // Null-guard for the schedule card (buildHomeModel filters its own input):
  // useGameCacheRealtime never admits null, but the page must not dereference one.
  const list = Array.isArray(games) ? games.filter(Boolean) : [];
  // Weekly leagues (NFL) read their own rolling slate; null until fetched or for a
  // daily league. The page resolves the cadence itself -- a hook returning rows for a
  // daily league can never leak into the daily card. When a slate exists it is MERGED
  // under the realtime rows (by String(id), realtime wins): the slate is a <=60s
  // snapshot, the prop is Realtime, so a LIVE score on the card always matches the
  // rail above it and an empty slate cannot hide a game the feed knows about.
  const { games: fetchedSlate, loading: slateLoading } = useLeagueSchedule(activeLeague);
  const isWeekly = scheduleCadence(activeLeague) === "weekly";
  const weeklySlate = isWeekly ? fetchedSlate : null;
  const cardGames = useMemo(() => {
    if (!weeklySlate) return list;
    const byId = new Map(weeklySlate.map((g) => [String(g.id), g]));
    for (const g of list) byId.set(String(g.id), g);
    return [...byId.values()];
  }, [weeklySlate, list]);

  const loading = dataSource === "loading";
  const stale = dataSource === "unavailable";

  // Everything bet-derived (mode, buckets, TO WIN, my-games-first rail, liveCount) comes from
  // the pure model; it re-derives on the same render the ticket does (Realtime -> props).
  // #1655 B3b (D-P2-1): the rail reads the window `games`; buckets and TO WIN read
  // `statusGames` (the window plus a NOT GRADED bet's old final rows; defaults to `games`).
  const model = useMemo(() => buildHomeModel({ bets, games, statusGames }), [bets, games, statusGames]);
  const { liveCount } = model;

  return (
    <div style={{ padding: "16px 16px 24px", animation: "fadeUp .3s ease" }}>
      {/* Heading */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h1 style={{ fontFamily: "'Big Shoulders Display',system-ui,sans-serif", fontWeight: 900, fontSize: 42, color: JT.text.primary, margin: 0, lineHeight: 1 }}>HOME</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {stale && <span style={{ ...JT.type.label, color: JT.status.missing.color }}>LAST-KNOWN{fmtUpdated(lastUpdated) ? ` · ${fmtUpdated(lastUpdated)}` : ""}</span>}
          <span data-testid="live-count" style={{
            ...JT.type.label,
            color: liveCount ? JT.status.live.color : JT.text.tertiary,
            background: JT.bg.well, border: `1px solid ${JT.border.mid}`, borderRadius: 999, padding: "3px 9px",
          }}>{liveCount} LIVE</span>
        </div>
      </div>

      {/* 1. Score rail */}
      <div data-testid="score-rail" style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginBottom: 16 }}>
        {loading
          ? [0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ flex: "0 0 auto", width: 112, height: 68, borderRadius: 14 }} />)
          : model.rail.length === 0
            ? <div style={{ fontSize: 12, color: JT.text.secondary, padding: "8px 0" }}>No games right now</div>
            : model.rail.map(({ game, legCount }, i) => <RailCard key={game.id || i} game={game} legCount={legCount} />)}
      </div>

      {/* 2. Hero -- state-driven (live-slip strip spec §6): sync in flight -> skeleton; sync failed ->
          the same error card Bets shows (never a false empty, #1117 / Ultron F3); active slips -> the
          count strip; none -> the Import nudge. */}
      {syncLoading
        ? <LiveSlipStrip loading />
        : syncError
          ? <div style={{ marginBottom: 16 }}><SyncErrorCard onRetry={onRetrySync} /></div>
          : model.mode === "slips"
            ? <LiveSlipStrip counts={model.counts} toWin={model.toWin} onViewBets={onViewBets} />
            : <ImportNudge onImport={onImport} />}

      {/* 3. Schedule card */}
      <div style={{ marginBottom: 16 }}>
        <ScheduleCardBound league={activeLeague} games={cardGames} loading={isWeekly && slateLoading} onPick={setLeague} />
      </div>

      {/* 4. The Wire — real ESPN headlines (spec 2026-08-09-news-feed §PR 3). Empty
          state never shows stale fake data; the affectsSlips pill stays dormant
          (categories stored but unread in v1); tap opens the story in-app. */}
      <div>
        <Eyebrow style={{ marginBottom: 8 }}>The Wire</Eyebrow>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {wire.length === 0 ? (
            <div style={{ fontSize: 12, color: JT.text.secondary, padding: "8px 0" }}>No headlines right now</div>
          ) : (
            wire.map((item) => (
              <div
                key={item.id}
                data-testid="wire-card"
                role="button"
                tabIndex={0}
                onClick={() => openInAppBrowser(item.url)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openInAppBrowser(item.url); } }}
                style={{ background: JT.bg.card, border: `1px solid ${JT.border.mid}`, borderRadius: 14, padding: 14, cursor: "pointer" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span data-testid="wire-byline" style={{ ...JT.type.label, color: JT.text.tertiary }}>
                    {leagueLabel(item.league)}{relativeAge(item.publishedAt) ? ` · ${relativeAge(item.publishedAt)}` : ""}
                  </span>
                  {item.affectsSlips != null && (
                    <span style={{ ...JT.type.label, color: JT.status.live.color, background: JT.status.live.dim, border: `1px solid ${JT.status.live.border}`, borderRadius: 20, padding: "2px 8px" }}>
                      AFFECTS {item.affectsSlips} SLIPS
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: JT.text.body, lineHeight: 1.4 }}>{item.headline}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Thin wrapper so ScheduleCard's league pills can drive the parent toggle without
// prop-drilling a bound handler through the pure card body above.
function ScheduleCardBound({ league, games, loading = false, onPick }) {
  const today = localToday();
  // One data-driven body for every league: the active league's rows inside its
  // scheduleWindow (today for a daily league, the rolling week for a weekly one).
  // Season state (offseason, bye week, no slate) is whatever game_cache says --
  // never copy baked into JSX.
  const weekly = scheduleCadence(league) === "weekly";
  const { from, to } = scheduleWindow(league);
  // gameDate must be a real YYYY-MM-DD string: the lexical range test (and even
  // RegExp.test) would coerce an array like ["2026-09-23"] through, and dayLabel
  // would then split a non-string; a timestamp string would render "Invalid Date".
  const validKey = (v) => typeof v === "string" && DATE_KEY.test(v);
  const todaysGames = games.filter((g) => g.league === league && validKey(g.gameDate) && g.gameDate >= from && g.gameDate <= to && SCHEDULE_STATUSES.has(g.status));
  // Weekly body groups by day, today first, then upcoming days, then yesterday.
  const dayOrder = (d) => (d === today ? 0 : d > today ? 1 : 2);
  const days = weekly
    ? [...new Set(todaysGames.map((g) => g.gameDate))].sort((a, b) => dayOrder(a) - dayOrder(b) || (dayOrder(a) === 2 ? b.localeCompare(a) : a.localeCompare(b)))
    : [today];
  return (
    <div data-testid="schedule-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Eyebrow>Schedule</Eyebrow>
        <div style={{ display: "flex", gap: 6 }}>
          {SUPPORTED_LEAGUES.map((l) => {
            const on = league === l;
            return (
              <button key={l} onClick={() => onPick(l)} aria-pressed={on} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "0 12px", minHeight: 44, borderRadius: 999,
                fontFamily: "inherit", ...JT.type.label, cursor: "pointer",
                background: on ? "linear-gradient(135deg,#1d4ed8,#4f46e5)" : JT.bg.well,
                border: `1px solid ${on ? "transparent" : JT.border.mid}`,
                color: on ? JT.text.primary : JT.text.tertiary,
              }}>
                <img src={leagueLogoUrl(l)} alt="" width={14} height={14} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                {leagueLabel(l)}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ background: JT.bg.panel, border: `1px solid ${JT.border.default}`, borderRadius: 14, padding: 16 }}>
        {todaysGames.length === 0 && loading ? (
          // First weekly read in flight and nothing to show yet: a skeleton, never the
          // empty copy (that is the very "No games scheduled" #1082 removed).
          <div data-testid="schedule-skeleton" className="skeleton" style={{ height: 44, borderRadius: 10 }} />
        ) : todaysGames.length === 0 ? (
          <div style={{ fontSize: 13, fontWeight: 700, color: JT.text.secondary }}>No games scheduled</div>
        ) : (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span data-testid={`${league}-count`} style={{ fontSize: 28, fontWeight: 900, color: "#60a5fa" }}>{todaysGames.length}</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: JT.text.secondary, letterSpacing: "1px" }}>{weekly ? "GAMES THIS WEEK" : "GAMES TODAY"}</span>
            </div>
            <div style={{ marginTop: 10 }}>
              {days.map((day) => {
                const dayGames = todaysGames.filter((g) => g.gameDate === day).sort(byStatusThenStart);
                const rows = dayGames.slice(0, 4);
                const more = dayGames.length - rows.length;
                return (
                  <div key={day} data-testid={weekly ? "schedule-day" : undefined} data-date={weekly ? day : undefined}>
                    {weekly && (
                      <div style={{ ...JT.type.label, color: JT.text.tertiary, padding: "8px 0 2px" }}>{dayLabel(day, today)}</div>
                    )}
                    {rows.map((game, i) => {
                      const live = game.status === "LIVE";
                      // A finished game reads its score; a scheduled one its start time.
                      const scored = live || game.status === "FINAL" || game.status === "OFF";
                      return (
                        <div key={game.id || i} style={{
                          display: "flex", justifyContent: "space-between", padding: "6px 0",
                          borderTop: i ? `1px dashed ${JT.border.mid}` : "none",
                          fontSize: 12, fontWeight: 700, color: live ? "#f1f5f9" : JT.text.secondary,
                        }}>
                          <span>{game.away?.team} @ {game.home?.team}</span>
                          <span style={{ color: live ? JT.status.live.color : JT.text.tertiary }}>
                            {scored ? `${game.away?.score}-${game.home?.score}` : (game.startTime || "")}
                          </span>
                        </div>
                      );
                    })}
                    {more > 0 && (
                      <div style={{ ...JT.type.label, color: JT.text.tertiary, padding: "6px 0 0" }}>+{more} more</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
